import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import axios from "axios";

import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EventIcon from "@mui/icons-material/Event";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import UndoIcon from "@mui/icons-material/Undo";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { allowedUsers } from "../components/constants";

import { SupervisorUid } from "../components/constants";

import EmployeeMultiSelect from "../components/attendance/EmployeeMultiSelect";

// to search and filter the specific request inside the whats link 
import { useSearchParams } from "react-router-dom";

const normalizeBaseUrl = (raw) => {
  if (!raw) return "";
  const trimmed = raw.trim().replace(/\/$/, "");
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const statusParamForFilter = (f) =>
  f === "pending" ? "1,2" : f === "approved" ? "3" : "1,2,3";

const sortOrderByStatusId = (id) => ({ 1: 0, 2: 1, 3: 2 }[Number(id)] ?? 9);

export default function ApproveLeavePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// to search and highlight the specific request ! 
  const [searchParams, setSearchParams] = useSearchParams();
  // linkEmp / linkFrom / linkTo → the filter from the link (empty when the page is opened normally).
  const linkEmp = searchParams.get("emp") || "";
  const linkFrom = searchParams.get("from") || "";
  const linkTo = searchParams.get("to") || linkFrom;

  const [monthRef, setMonthRef] = useState(() =>
    linkFrom && dayjs(linkFrom).isValid() ? dayjs(linkFrom) : dayjs()
  );
  // loadedOnce → stops “no longer available” from flashing before the data arrives
  const [loadedOnce, setLoadedOnce] = useState(false);

  const [search, setSearch] = useState("");

  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [selectedEmps, setSelectedEmps] = useState([]);

  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [localStatus, setLocalStatus] = useState({});
  const [spr, setSpr] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  //confirm dialog
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, row: null });
  const spr_code =
    localStorage.getItem("shared_empid") ||
    localStorage.getItem("saved_username") ||
    "";

  const baseUrl = useMemo(
    () => normalizeBaseUrl(localStorage.getItem("saved_sett_api_url") || ""),
    [],
  );

  const [supervisor] = useState(
    localStorage.getItem("saved_username") ||
      localStorage.getItem("saved_password") ||
      "",
  );

  const isHr = allowedUsers.includes(localStorage.getItem("saved_username") || "");

  const year = monthRef.year();
  const month = monthRef.month() + 1;
  const androidId = localStorage.getItem("saved_sett_mobilenumber") || "";

  const hrRule = useMemo(() => {
    const r = (spr || []).find((x) => Number(x?.spr_hr) === 1);
    return {
      enabled: !!r,
      afterDays: Math.max(0, Number(r?.after_days) || 0),
    };
  }, [spr]);

  const canShowApprove = (row) => {
    if (!hrRule.enabled) return true;
    const cellDate = dayjs(row?.FromDate, "YYYY/MM/DD");
    if (!cellDate.isValid()) return false;
    const diffDays = dayjs().diff(cellDate, "day");
    return diffDays >= hrRule.afterDays;
  };

  const isPartialByMe = (row) => {
    if (!row?.approve_obj) return false;

    const obj =
      typeof row.approve_obj === "string"
        ? JSON.parse(row.approve_obj)
        : row.approve_obj;

    return (
      obj?.[spr_code]?.aprv_submit === 1 && obj?.[spr_code]?.aprv_type === "p"
    );
  };

  //*it looks in the leave's approval history for your entry and
  //*  returns its type: "p" (your approval was partial), 
  //* "c" (your approval was the final one), or "" (you haven't approved).
 
  const myApprovalType = (row) => {
  if (!row?.approve_obj) return "";
  const obj =
    typeof row.approve_obj === "string"
      ? JSON.parse(row.approve_obj)
      : row.approve_obj;
  const mine = obj?.[isHr ? "HR" : spr_code];
  return mine?.aprv_submit === 1 ? String(mine.aprv_type || "").toLowerCase() : "";
};

  const renderTime = (t) => {
    if (!t) return "";
    const s = String(t);
    return s.length >= 5 ? s.slice(0, 5) : s;
  };

  const renderDay = (yyyyMMDD_slash) => {
    const d = dayjs(yyyyMMDD_slash, "YYYY/MM/DD");
    if (!d.isValid()) return "";
    return d.format("dddd");
  };

  const aprvTypeOf = (row) => String(row?.aprv_type || "").toLowerCase();

  const isFinalApproved = (row) =>
    localStatus[row.ID] === "approved" || aprvTypeOf(row) === "c";

  const isPartialApproved = (row) =>
    !isFinalApproved(row) && aprvTypeOf(row) === "p";

  const isApproved = (row) => isFinalApproved(row);

  const canApproveRow = (row) => {
    const canAct = Number(row?.canAct ?? 0) === 1;
    const canByHr = canShowApprove(row);
    const partialByMe = isPartialByMe(row);
    return canAct && canByHr && !partialByMe && !isApproved(row);
  };

 // filter 
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      const matchesEmp =!selectedEmps.length || selectedEmps.some((e)=> Number(e.key) === Number(r.EmpId));

      let matchesStatus =true;
      if (statusFilter === "pending") {
        matchesStatus = !isFinalApproved(r) && !isPartialApproved(r);
      }else if (statusFilter === "partial") {
        matchesStatus = isPartialApproved(r);
      }else if (statusFilter === "approved") {
        matchesStatus = isFinalApproved(r);
      }

       // "all" leaves matchesStatus as true

      return matchesEmp && matchesStatus;

    })
    // if (!selectedEmps.length) return rows;
    // return rows.filter((r) => selectedEmps.some((e) => Number(e.key) === Number(r.EmpId)));
  },[rows, selectedEmps,  statusFilter, localStatus]);



// Filter 
  const selectableRows = useMemo(
    () => filteredRows.filter((row) => canApproveRow(row)),
    [filteredRows, localStatus, spr, hrRule],
  );

  const selectableIds = useMemo(
    () => selectableRows.map((r) => r.ID),
    [selectableRows],
  );

  const allSelectableChecked =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIds.includes(id));

  const someSelectableChecked =
    selectableIds.some((id) => selectedIds.includes(id)) &&
    !allSelectableChecked;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(selectableIds);
    } else {
      setSelectedIds([]);
    }
  };

  const toggleSelectRow = (rowId) => {
    setSelectedIds((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId],
    );
  };


//! add filter employee from approve visit page 
// load the employee list 
useEffect(() => {
  if (!baseUrl) return;
  axios
    .get(`${baseUrl}/api/employees`, { params: { excludeUids: SupervisorUid.join(",") } })
    .then((res) => setEmployeeOptions(Array.isArray(res.data?.data) ? res.data.data : []))
    .catch(() => setEmployeeOptions([]));
}, [baseUrl]);

// EmployeeMultiSelect (same component AllAttendanceNewPage's HR filters
// use) expects {key, label} options — map the raw {empId, fullName}
// shape once here, without touching the shared component or backend.
const employeeMultiOptions = useMemo(
  () => employeeOptions.map((e) => ({ key: e.empId, label: e.fullName })),
  [employeeOptions],
);


const hasRestoredEmpsRef = useRef(false);

useEffect(() => {
  if (!employeeMultiOptions.length) return;
  if (hasRestoredEmpsRef.current) return; // only ever restore once
  hasRestoredEmpsRef.current = true;
  // When the page opens from a link, it selects that employee instead of your saved selection
  if (linkEmp) {
    const match = employeeMultiOptions.filter((e) => String(e.key) === String(linkEmp));
    if (match.length) setSelectedEmps(match);
    return;
  }



  const savedIds = localStorage.getItem("leaveApproval_selectedEmpIds");
  if (savedIds) {
    try {
      const ids = JSON.parse(savedIds);
      if (Array.isArray(ids) && ids.length) {
        const matches = employeeMultiOptions.filter((e) => ids.includes(String(e.key)));
        if (matches.length) setSelectedEmps(matches);
      }
    } catch {
      // ignore malformed saved value
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [employeeMultiOptions]);

useEffect(() => {
  // Don't touch localStorage until the restore attempt above has
  // actually happened — otherwise this fires on mount with the
  // initial empty [] and deletes the saved selection before it can
  // ever be restored.
  if (!hasRestoredEmpsRef.current) return;
  if (!linkEmp) return ; 

  if (selectedEmps.length) {
    localStorage.setItem(
      "leaveApproval_selectedEmpIds",
      JSON.stringify(selectedEmps.map((e) => String(e.key))),
    );
  } else {
    localStorage.removeItem("leaveApproval_selectedEmpIds");
  }
}, [selectedEmps]);




//!end end end 
  

  const fetchRows = async () => {
    if (!baseUrl) return;
    setPending(true);

    try {
      const res = await axios.get(`${baseUrl}/api/leave-approvals`, {
        params: {
          month,
          year,
          status: statusParamForFilter(statusFilter),
          // q: search || "",
          supervisor,
          isHr,
        },
        withCredentials: true,
      });

      const payload = res.data;
      const sprList = Array.isArray(payload?.spr) ? payload.spr : [];

      const data = (Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : []
      ).filter((r) => !SupervisorUid.includes(String(r.EmpId)));

      data.sort((a, b) => {
        const aKey = sortOrderByStatusId(a.LeaveStatus);
        const bKey = sortOrderByStatusId(b.LeaveStatus);
        if (aKey !== bKey) return aKey - bKey;
        if (a.FromDate !== b.FromDate)
          return (a.FromDate || "").localeCompare(b.FromDate || "");
        //return (a.ID || 0) - (b.ID || 0);
        return String(a.ID || "").localeCompare(String(b.ID || ""));
      });

      setRows(data);
      setSpr(sprList);
      setLocalStatus({});
      //setSelectedIds([]);
      setSelectedIds((prev) => prev.filter((id) => data.some((r) => r.ID === id)));
    } catch (e) {
      setSnack({
        open: true,
        msg:
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Failed to load leave approvals",
        sev: "error",
      });
    } finally {
      setPending(false);
      setLoadedOnce(true);
    }
  };

 

  useEffect(() => {
    fetchRows();
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchRows, 350);
    return () => clearTimeout(t);
  }, [monthRef, statusFilter]);


  const handleApprove = async (row) => {
    //setLocalStatus((m) => ({ ...m, [row.ID]: "approved" }));
    try {
      await axios.post(`${baseUrl}/api/leave-approvals/${row.ID}/approve`, {
        androidId,
        spr_code,
        isHr,
        hrUserIds: allowedUsers,
      });

      setSelectedIds((prev) => prev.filter((id) => id !== row.ID));
      setSnack({ open: true, msg: "Leave approved", sev: "success" });
      await fetchRows();
    } catch (e) {
      //setLocalStatus((m) => ({ ...m, [row.ID]: "pending" }));
      setSnack({
        open: true,
        msg:
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Approve failed",
        sev: "error",
      });
    }
  };

  const handleDelete = async (row) => {
    setDeleteConfirm({ open: true, row });
  };  
  const confirmDelete = async () => {
    const row = deleteConfirm.row;
    setDeleteConfirm({ open: false, row:null}); 
    try {
      await axios.delete(`${baseUrl}/api/leave-approvals/${row.ID}`, {
        data: { androidId, spr_code },
        withCredentials: true,
      });
      setRows((prev) => prev.filter((r) => r.ID !== row.ID));
      setSelectedIds((prev) => prev.filter((id) => id !== row.ID));
      setSnack({ open: true, msg: "Leave request deleted.", sev: "success" });
    } catch (e) {
      setSnack({
        open: true,
        msg: e.response?.data?.error || e.response?.data?.message || "Delete failed",
        sev: "error",
      });
    }
  };

  const handleCancel = async (row) => {
    //setLocalStatus((m) => ({ ...m, [row.ID]: "pending" }));
    try {
      // const spr_code =
      //   localStorage.getItem("shared_empid") ||
      //   localStorage.getItem("saved_username") ||
      //   "";

      await axios.post(`${baseUrl}/api/leave-approvals/${row.ID}/cancel`, {
        androidId,
        spr_code,
        isHr,
      });

      setSnack({
        open: true,
        msg: "Approval canceled (back to pending)",
        sev: "info",
      });
       await fetchRows();
    } catch (e) {
      //setLocalStatus((m) => ({ ...m, [row.ID]: "approved" }));
      setSnack({
        open: true,
        msg:
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Cancel failed",
        sev: "error",
      });
    }
  };

  const handleApproveAll = async () => {
  if (!baseUrl) return;

  const targets = rows.filter(
    (r) => selectedIds.includes(r.ID) && canApproveRow(r),
  );

  if (targets.length === 0) {
    setSnack({
      open: true,
      msg: "Please select pending requests to approve.",
      sev: "info",
    });
    return;
  }

  const payload = {
    androidId,
    spr_code,
    isHr,
    hrUserIds: allowedUsers,
    ids: targets.map((r) => r.ID),
    rows: targets.map((r) => ({
      ID: r.ID,
      EmpId: r.EmpId,
      FromDate: r.FromDate,
      FromTime: r.FromTime,
      ToTime: r.ToTime,
      LeaveType: r.LeaveType,
      LeaveTypeName: r.LeaveTypeName,
      EmpName: r.EmpName,
    })),
  };

  // setLocalStatus((m) => {
  //   const next = { ...m };
  //   targets.forEach((r) => {
  //     next[r.ID] = "approved";
  //   });
  //   return next;
  // });

  setBulkApproving(true);

  try {
    const res = await axios.post(
      `${baseUrl}/api/leave-approvals/bulk-approve`,
      payload,
    );

    const approvedIds = Array.isArray(res?.data?.approvedIds)
      ? res.data.approvedIds
      : targets.map((r) => r.ID);

    // const failedIds = Array.isArray(res?.data?.failedIds)
    //   ? res.data.failedIds
    //   : [];

    // if (failedIds.length > 0) {
    //   setLocalStatus((m) => {
    //     const next = { ...m };
    //     failedIds.forEach((id) => {
    //       next[id] = "pending";
    //     });
    //     return next;
    //   });

    //   setSelectedIds(failedIds);

    const failedIds = Array.isArray(res?.data?.failedItems)
      ? res.data.failedItems.map((f) => f.requestId)
      : [];

    if (failedIds.length > 0) {
      setSelectedIds(failedIds);

      setSnack({
        open: true,
        msg: `Approved ${approvedIds.length}, failed ${failedIds.length}.`,
        sev: approvedIds.length ? "warning" : "error",
      });
    } else {
      setSelectedIds([]);
      setSnack({
        open: true,
        msg: `Approved ${approvedIds.length} request(s).`,
        sev: "success",
      });
    }
  } catch (e) {
    // const ids = targets.map((r) => r.ID);

    // setLocalStatus((m) => {
    //   const next = { ...m };
    //   ids.forEach((id) => {
    //     next[id] = "pending";
    //   });
    //   return next;
    // });
    setSnack({
      open: true,
      msg:
        e.response?.data?.error ||
        e.response?.data?.message ||
        "Bulk approve failed.",
      sev: "error",
    });
  } finally {
    setBulkApproving(false);
    await fetchRows();
  }
};

  const leaveTypeChipColor = (label) => {
    const v = String(label || "").toLowerCase();
    if (!v) return "default";
    if (v.includes("sick")) return "error";
    if (v.includes("annual") || v.includes("vacation")) return "info";
    if (v.includes("unpaid")) return "warning";
    return "primary";
  };

  const statusChip = (row) => {
    if (isFinalApproved(row)) return { label: "Approved", color: "success" };
    if (isPartialApproved(row)) return { label: "Partial", color: "info" };
    return { label: "Pending", color: "warning" };
  };

  const counts = rows.reduce(
    (acc, r) => {
      if (isFinalApproved(r)) acc.approved += 1;
      else if (isPartialApproved(r)) acc.partial += 1;
      else acc.pending += 1;
      return acc;
    },
    { pending: 0, partial: 0, approved: 0 },
  );

  const approveAllDisabled =
    !baseUrl || pending || bulkApproving || selectedIds.length === 0;

    //* Helpers: which rows to highlight, not found, show all
    //*same employee, and a date between from and to. FromDate comes as 2026/10/27, 
    //* so / becomes - to compare with the link’s dates

    const isLinkedRow = (row) => {
      if (!linkEmp) return false;
      if (String(row.EmpId) !== String(linkEmp)) return false;
      const d = String(row.FromDate || "").replace(/\//g, "-");
      return (!linkFrom || d >= linkFrom) && (!linkTo || d <= linkTo);
    };
    //* the data has loaded, and no row matches
    const linkNotFound = Boolean(linkEmp) && loadedOnce && !pending && !rows.some(isLinkedRow);
    //* Show all”: clears the employee filter and removes the filter from the URL
    const clearLink = () => {
      setSelectedEmps([]);
      setSearchParams({});
    };

    const renderActions = (row) => {
    const approved = isApproved(row);
    const canAct = Number(row?.canAct ?? 0) === 1;
    const canByHr = canShowApprove(row);
    const partialByMe = isPartialByMe(row);
    const canApprove = canAct && canByHr && !partialByMe && !approved;

    return (
      <>
        {partialByMe || (approved && myApprovalType(row) === "c") ? (
          <Tooltip title="Cancel approval (undo)">
            <span>
              <IconButton
                color="warning"
                onClick={() => handleCancel(row)}
                size="small"
                sx={{
                  backgroundColor: "#fff8e1",
                  "&:hover": { backgroundColor: "#ffecb3" },
                }}
                disabled={!baseUrl || bulkApproving}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : !canApprove ? (
          <Tooltip
            title={
              partialByMe
                ? "Already partially approved by you"
                : "Not allowed"
            }
          >
            <span>
              <IconButton
                color="success"
                size="small"
                sx={{ backgroundColor: "#e8f5e9" }}
                disabled
              >
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Approve">
            <span>
              <IconButton
                color="success"
                onClick={() => handleApprove(row)}
                size="small"
                sx={{
                  backgroundColor: "#e8f5e9",
                  "&:hover": { backgroundColor: "#c8e6c9" },
                }}
                disabled={!baseUrl || bulkApproving}
              >
                <CheckCircleOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title="Delete leave request">
          <span>
            <IconButton
              color="error"
              onClick={() => handleDelete(row)}
              size="small"
              sx={{
                backgroundColor: "#ffebee",
                "&:hover": { backgroundColor: "#ffcdd2" },
              }}
              disabled={!baseUrl || bulkApproving}
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  };  

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        pt: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
        background: {
          xs: "linear-gradient(180deg, #e0f2f1 0%, #ffffff 40%)",
          md: "linear-gradient(135deg, #e0f2f1 0%, #ffffff 55%)",
        },
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 1200,
          p: { xs: 1.5, sm: 3 },
          borderRadius: 4,
          overflow: "hidden",
          background: "linear-gradient(180deg, #ffffff 0%, #fbfffe 100%)",
          boxShadow: "0 20px 60px rgba(0,0,0,.12)",
          border: "1px solid #b2dfdb",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
          spacing={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <EventIcon sx={{ color: "#00796b" }} />
            <Typography variant="h6" fontWeight={800} color="#004d40">
              Approve Leave Requests
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              label={`Pending: ${counts.pending}`}
              color="warning"
              variant="filled"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`Partial: ${counts.partial}`}
              color="info"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`Approved: ${counts.approved}`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              label={`Selected: ${selectedIds.length}`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
            {!isMobile && (
            <Tooltip
              title={approveAllDisabled ? "" : "Approve selected requests"}
            >
              <span>
                <Button
                  onClick={handleApproveAll}
                  startIcon={
                    bulkApproving ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DoneAllIcon />
                    )
                  }
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={approveAllDisabled}
                  sx={{
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  Approve Selected
                </Button>
              </span>
            </Tooltip>
            )}
          </Stack>
        </Stack>

        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            borderRadius: 2,
            border: "1px solid #b2dfdb",
            background: "linear-gradient(180deg, #f7fffd 0%, #f1fdfb 100%)",
            boxShadow: "0 6px 20px rgba(0,0,0,.06)",
            "& .MuiFormControl-root": { m: 0 },
            "& .MuiInputBase-root": { height: 36, fontSize: 14 },
            "& .MuiInputBase-input": { py: 0.5 },
            "& .MuiInputLabel-root": { fontSize: 12 },
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1}
            useFlexGap
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 140 }}
            >
              <EventIcon sx={{ color: "#00796b" }} />
              <Typography
                variant="subtitle2"
                sx={{ color: "#00796b", fontWeight: 700 }}
              >
                Filters
              </Typography>
            </Stack>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                views={["year", "month"]}
                label="Month"
                minDate={dayjs("2022-01-01")}
                maxDate={dayjs().add(1, "year").endOf("year")}
                value={monthRef}
                onChange={(v) => setMonthRef(v || dayjs())}
                slots={{ openPickerIcon: EventIcon }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: false,
                    sx: {
                      width: { xs: "100%", sm: 200 },
                      backgroundColor: "white",
                      borderRadius: 2,
                    },
                  },
                  openPickerButton: { size: "small" },
                }}
              />
            </LocalizationProvider>

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ backgroundColor: "white", borderRadius: 2 }}
              >
                <MenuItem value="all">All</MenuItem>
                {/* <MenuItem value="pending">Pending</MenuItem> */}
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                {/* <MenuItem value="all">All (Pending + Approved)</MenuItem> */}
                <MenuItem value="pending">Pending</MenuItem>
                
              </Select>
            </FormControl>

           <EmployeeMultiSelect
              employeeOptions={employeeMultiOptions}
              selectedEmps={selectedEmps}
              onChange={setSelectedEmps}
            />

            <Box sx={{ flex: 1, display: { xs: "none", sm: "block" } }} />

            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={fetchRows}
                  disabled={pending || bulkApproving}
                  size="small"
                  sx={{
                    border: "1px solid #b2dfdb",
                    borderRadius: 2,
                    backgroundColor: "white",
                    "&:hover": { backgroundColor: "#f0fffb" },
                  }}
                >
                  {pending ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {linkEmp && (
          <Alert
            severity={linkNotFound ? "warning" : "info"}
            sx={{ mb: 1.5, borderRadius: 2, alignItems: "center" }}
            action={
              <Button size="small" onClick={clearLink} sx={{ fontWeight: 700, textTransform: "none" }}>
                Show all
              </Button>
            }
          >
            {linkNotFound ? "This request is no longer available." : "Showing the request from your message."}
          </Alert>
        )}

      {!isMobile ? (
        <TableContainer
          component={Paper}
          elevation={3}
          sx={{ borderRadius: 2, overflowX: "auto" }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#00796b" }}>
                <TableCell
                  sx={{
                    width: 60,
                    textAlign: "center",
                    borderBottom: "1px solid #004d40",
                    py: 1,
                    px: 1,
                  }}
                >
                  <Checkbox
                    checked={allSelectableChecked}
                    indeterminate={someSelectableChecked}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    disabled={selectableIds.length === 0}
                    sx={{ color: "white", "&.Mui-checked": { color: "white" } }}
                  />
                </TableCell>

                <TableCell sx={{ display: "none" }}>EmpId</TableCell>

                <TableCell
                  sx={{
                    width: 220,
                    fontWeight: "bold",
                    color: "white",
                    fontSize: { xs: 10, sm: 13, md: 14 },
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    borderBottom: "1px solid #004d40",
                    py: 1,
                    px: 1,
                    textAlign: "center",
                  }}
                >
                  Employee
                </TableCell>

                <TableCell
                  sx={{
                    width: 120,
                    fontWeight: "bold",
                    color: "white",
                    fontSize: { xs: 10, sm: 13, md: 14 },
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    borderBottom: "1px solid #004d40",
                    py: 1,
                    px: 1,
                    textAlign: "center",
                  }}
                >
                  Date
                </TableCell>

                <TableCell
                  sx={{
                    width: 110,
                    fontWeight: "bold",
                    color: "white",
                    fontSize: { xs: 10, sm: 13, md: 14 },
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    borderBottom: "1px solid #004d40",
                    py: 1,
                    px: 1,
                    textAlign: "center",
                  }}
                >
                  Day
                </TableCell>

                <TableCell
                  sx={{
                    width: 160,
                    fontWeight: "bold",
                    color: "white",
                    fontSize: { xs: 10, sm: 13, md: 14 },
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    borderBottom: "1px solid #004d40",
                    py: 1,
                    px: 1,
                    textAlign: "center",
                  }}
                >
                  Time In/Out
                </TableCell>

                <TableCell
                  sx={{
                    width: 170,
                    fontWeight: "bold",
                    color: "white",
                    fontSize: { xs: 10, sm: 13, md: 14 },
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    borderBottom: "1px solid #004d40",
                    py: 1,
                    px: 1,
                    textAlign: "center",
                  }}
                >
                  Leave Type
                </TableCell>

                <TableCell
                  sx={{
                    width: 140,
                    fontWeight: "bold",
                    color: "white",
                    fontSize: { xs: 10, sm: 13, md: 14 },
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    borderBottom: "1px solid #004d40",
                    py: 1,
                    px: 1,
                    textAlign: "center",
                  }}
                >
                  Status
                </TableCell>

                <TableCell
                  sx={{
                    width: 100,
                    fontWeight: "bold",
                    color: "white",
                    fontSize: { xs: 10, sm: 13, md: 14 },
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    borderBottom: "1px solid #004d40",
                    py: 1,
                    px: 1,
                    textAlign: "center",
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* {rows.length === 0 && !pending && ( */}
              {filteredRows.length === 0 && !pending && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                    sx={{ py: 4, color: "text.secondary" }}
                  >
                    No leave requests for this filter
                  </TableCell>
                </TableRow>
              )}

              {filteredRows.map((row) => {
                const approved = isApproved(row);
                const leaveLabel = row.LeaveTypeDesc || row.LeaveType || "";
                const leavetypename = row.LeaveTypeName || "";
                const sc = statusChip(row);
                const rowCanApprove = canApproveRow(row);
                const checked = selectedIds.includes(row.ID);

                return (
                  <TableRow
                    key={row.ID}
                    hover
                    //==== to highlight specific request !!! 
                    sx={{
                      backgroundColor: isLinkedRow(row) ? "#fff8e1" : undefined,
                      "&:nth-of-type(odd)": { backgroundColor: isLinkedRow(row) ? "#fff8e1" : "#f1fdfb" },
                      "& td:first-of-type": { borderLeft: isLinkedRow(row) ? "4px solid #f9a825" : undefined },
                      "& td": {
                        fontSize: { xs: 8, sm: 14 },
                        py: 0.5,
                        px: 1,
                        verticalAlign: "top",
                      },
                    }}
                  >
                    <TableCell sx={{ textAlign: "center" }}>
                      <Checkbox
                        checked={checked}
                        onChange={() => toggleSelectRow(row.ID)}
                        disabled={!rowCanApprove || bulkApproving}
                      />
                    </TableCell>

                    <TableCell sx={{ display: "none" }}>{row.EmpId}</TableCell>

                    <TableCell sx={{ textAlign: "center" }}>
                      {row.EmpName || row.EmpCode || row.EmpId}
                    </TableCell>

                    <TableCell sx={{ textAlign: "center" }}>
                      {row.FromDate || ""}
                    </TableCell>

                    <TableCell sx={{ textAlign: "center" }}>
                      {renderDay(row.FromDate)}
                    </TableCell>

                    <TableCell sx={{ textAlign: "center" }}>
                      {renderTime(row.FromTime)} – {renderTime(row.ToTime)}
                    </TableCell>

                    <TableCell sx={{ textAlign: "center" }}>
                      <Chip
                        label={leavetypename}
                        size="small"
                        color={leaveTypeChipColor(leaveLabel)}
                        sx={{
                          fontSize: { xs: 8, sm: 12 },
                          height: 20,
                          fontWeight: "bold",
                          maxWidth: 180,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ textAlign: "center" }}>
                      <Chip
                        label={sc.label}
                        color={sc.color}
                        size="small"
                        sx={{
                          fontSize: { xs: 8, sm: 12 },
                          height: 20,
                          fontWeight: "bold",
                          width: { xs: 100, sm: 120 },
                          display: "flex",
                          justifyContent: "center",
                        }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Stack direction="row" justifyContent="center" spacing={0.5}>
                          {renderActions(row)}
                      {/* {(() => {
                        const canAct = Number(row?.canAct ?? 0) === 1;
                        const canByHr = canShowApprove(row);
                        const partialByMe = isPartialByMe(row);
                        // const canApprove = canAct && canByHr && !partialByMe;
                        const canApprove = canAct && canByHr && !partialByMe && !approved;

                      return (
                            <> */}
                              {/* Approve / Cancel / Disabled button */}
                              {/* {partialByMe || (approved && myApprovalType(row) === "c") ? (
                                <Tooltip title="Cancel approval (undo)">
                                  <span>
                                    <IconButton
                                      color="warning"
                                      onClick={() => handleCancel(row)}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#fff8e1",
                                        "&:hover": { backgroundColor: "#ffecb3" },
                                      }}
                                      disabled={!baseUrl || bulkApproving}
                                    >
                                      <UndoIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : !canApprove ? (
                                <Tooltip
                                  title={
                                    partialByMe
                                      ? "Already partially approved by you"
                                      : "Not allowed"
                                  }
                                >
                                  <span>
                                    <IconButton
                                      color="success"
                                      size="small"
                                      sx={{ backgroundColor: "#e8f5e9" }}
                                      disabled
                                    >
                                      <CheckCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Approve">
                                  <span>
                                    <IconButton
                                      color="success"
                                      onClick={() => handleApprove(row)}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#e8f5e9",
                                        "&:hover": { backgroundColor: "#c8e6c9" },
                                      }}
                                      disabled={!baseUrl || bulkApproving}
                                    >
                                      <CheckCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )} */}

                              {/* Delete button — always visible */}
                              {/* <Tooltip title="Delete leave request">
                                <span>
                                  <IconButton
                                    color="error"
                                    onClick={() => handleDelete(row)}
                                    size="small"
                                    sx={{
                                      backgroundColor: "#ffebee",
                                      "&:hover": { backgroundColor: "#ffcdd2" },
                                    }}
                                    disabled={!baseUrl || bulkApproving}
                                  >
                                    <DeleteRoundedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip> */}
                            {/* </> */}
                    {/* //       );
                    //     })()} */}
                       </Stack>
                     </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
                    </Table>
        </TableContainer>
        ) : (
          <Stack spacing={1.25}>
            {selectableIds.length > 0 && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={allSelectableChecked}
                    indeterminate={someSelectableChecked}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    disabled={bulkApproving}
                  />
                  <Typography variant="body2" fontWeight={700} color="#004d40">
                    Select all
                  </Typography>
                </Stack>

                <Button
                  onClick={handleApproveAll}
                  startIcon={
                    bulkApproving ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DoneAllIcon />
                    )
                  }
                  variant="contained"
                  color="success"
                  size="small"
                  disabled={approveAllDisabled}
                  sx={{
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  Approve Selected
                </Button>
              </Stack>
            )}

            {filteredRows.length === 0 && !pending && (
              <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                No leave requests for this filter
              </Typography>
            )}

            {filteredRows.map((row) => {
              const sc = statusChip(row);
              const rowCanApprove = canApproveRow(row);
              const checked = selectedIds.includes(row.ID);
              const leaveLabel = row.LeaveTypeDesc || row.LeaveType || "";

              return (
                <Paper
                  key={row.ID}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 3,
                    // highlight on mobile card !!!
                    border: isLinkedRow(row) ? "2px solid #f9a825" : "1px solid #b2dfdb",
                    backgroundColor: isLinkedRow(row) ? "#fff8e1" : "#fbfffe",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleSelectRow(row.ID)}
                        disabled={!rowCanApprove || bulkApproving}
                        sx={{ p: 0.5 }}
                      />
                      <Typography fontWeight={700} sx={{ fontSize: 14 }} noWrap>
                        {row.EmpName || row.EmpCode || row.EmpId}
                      </Typography>
                    </Stack>
                    <Chip label={sc.label} color={sc.color} size="small" sx={{ fontWeight: 700 }} />
                  </Stack>

                  <Stack direction="row" spacing={1.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" color="text.secondary">
                      {row.FromDate} ({renderDay(row.FromDate)})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {renderTime(row.FromTime)} – {renderTime(row.ToTime)}
                    </Typography>
                  </Stack>

                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                    <Chip
                      label={row.LeaveTypeName || ""}
                      size="small"
                      color={leaveTypeChipColor(leaveLabel)}
                      sx={{ fontWeight: 700 }}
                    />
                    <Stack direction="row" spacing={0.5}>
                      {renderActions(row)}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}
      </Paper>
      
      {/* Delete confirm dialog */}
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, row: null })}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete leave request?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, row: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}